/**
 * AdminPdfSettings — PDF Angebot/Bestellung Ayarları
 *
 * Admin kullanıcısı burada PDF header (logo, başlıklar),
 * footer (firma, banka), selamlama/kapanış metinlerini düzenler.
 */

import React, { useState, useRef, useEffect } from 'react';
import PageShell from '@/components/ui/PageShell';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
    Save, Upload, ImageIcon, FileText, Building2, CreditCard,
    MessageSquare, Eye, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { clearPdfSettingsCache } from '@/components/orders/OrderPDFGenerator';
import VorlagenEditor from '@/components/pdf/VorlagenEditor';
import { vorschauHtml } from '@/components/pdf/vorlagenVorschau';
import { DOKUMENT_TYPEN } from '@/lib/dokumentVorlagen';
import { clearPdfSettingsCache as clearSharedPdfCache, loadPdfSettingsRow } from '@/lib/pdfRenderer';
import { safeEntityUpdate, safeEntityCreate } from '@/lib/safeUpdate';

// Varsayılan ayarlar (migration ile aynı)
const DEFAULTS = {
    header_logo_url: '',
    header_left_title: 'I M INMOTION',
    header_left_tagline: 'IMMER IN BEWEGUNG',
    header_right_title: 'GROSSHANDEL | HORECA',
    header_right_subtitle: 'VERPACKUNG | HYGIENE | REINIGUNGSMITTEL',
    company_name: 'InMotion Großhandel UG',
    street: 'Asbach 19',
    zip: '86510',
    city: 'Ried',
    country: 'Deutschland',
    phone: '089 357 650 - 41',
    email: 'info@inmotion-handel.de',
    website: 'www.inmotion-handel.de',
    registry_city: 'Augsburg',
    hr_nr: 'HRB40485',
    steuer_nr: '102/129/30143',
    geschaeftsfuehrer: 'Bünyamin Cicekdag',
    bank_name: 'DEUTSCHE BANK',
    iban: 'DE13 7207 0001 0101 2160 00',
    bic: 'DEUTDEMM720',
    ansprechpartner: 'Buchhaltung',
    greeting_offer: 'vielen Dank für Ihre Anfrage und das Interesse an unseren Leistungen.\nGerne unterbreiten wir Ihnen hiermit folgendes Angebot:',
    greeting_order: 'vielen Dank für Ihre Bestellung. Wir bestätigen folgenden Auftrag:',
    greeting_lieferschein: 'anbei erhalten Sie die bestellte Ware mit diesem Lieferschein.',
    closing_offer: 'Dieses Angebot ist 30 Tage gültig. Wir freuen uns auf Ihren Auftrag.',
    closing_order: 'Vielen Dank für Ihr Vertrauen.',
    closing_lieferschein: 'Bitte prüfen Sie die Lieferung auf Vollständigkeit und Unversehrtheit.',
    vorlagen: {},
};

// Section component
function Section({ icon: Icon, title, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card className="overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-900 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-800">{title}</h3>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {open && <div className="p-5 space-y-4">{children}</div>}
        </Card>
    );
}

// Field label + input
function Field({ label, value, onChange, type = 'text', placeholder, multiline = false }) {
    return (
        <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-600">{label}</label>
            {multiline ? (
                <Textarea
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={3}
                    className="resize-none"
                />
            ) : (
                <Input
                    type={type}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                />
            )}
        </div>
    );
}

export default function AdminPdfSettings() {
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);
    const [settings, setSettings] = useState({ ...DEFAULTS });
    const [showPreview, setShowPreview] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [settingsId, setSettingsId] = useState(null);
    const [activeTyp, setActiveTyp] = useState('angebot'); // Dokumenttyp (lib/dokumentVorlagen)

    // Load existing settings — älteste Zeile ist maßgeblich (wie in allen PDFs).
    // TanStack Query v5 kennt kein onSuccess mehr → Formular per Effekt füllen.
    const { data: loaded, isLoading, error: loadError } = useQuery({
        queryKey: ['pdf-settings'],
        queryFn: loadPdfSettingsRow,
    });
    useEffect(() => {
        const data = loaded?.row;
        if (!data) return;
        setSettingsId(data.id);
        // Merge loaded data with defaults (null values fall back to default)
        const merged = { ...DEFAULTS };
        Object.keys(DEFAULTS).forEach((key) => {
            if (data[key] != null && data[key] !== '') merged[key] = data[key];
        });
        merged.vorlagen = data.vorlagen && typeof data.vorlagen === 'object' ? data.vorlagen : {};
        setSettings(merged);
        setHasChanges(false);
    }, [loaded]);

    // Update a field
    const updateField = (key, value) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async () => {
            const payload = Object.fromEntries(
                Object.entries({ ...settings, updated_date: new Date().toISOString() })
                    .filter(([key]) => key !== 'id' && key !== 'created_date')
            );
            // Fehlende Spalten (Migration 20261103_02 noch nicht ausgeführt) blockieren
            // nicht das ganze Speichern – sie werden übersprungen und gemeldet.
            return settingsId
                ? safeEntityUpdate(base44.entities.PdfSetting, settingsId, payload)
                : safeEntityCreate(base44.entities.PdfSetting, payload);
        },
        onSuccess: ({ data, skipped }) => {
            if (data?.id) setSettingsId(data.id);
            if (skipped?.length) {
                toast.warning('Nicht gespeichert: ' + skipped.join(', '), {
                    description: 'Datenbank-Update fehlt (Migration 20261103_02_pdf_settings_texte.sql).',
                });
            }
            queryClient.invalidateQueries(['pdf-settings']);
            // Beide PDF-Caches leeren — Änderungen wirken sofort in ALLEN
            // Dokumenten (Angebot/Bestellung UND Rechnung/LS/KM/Mahnung/GS)
            clearPdfSettingsCache();
            clearSharedPdfCache();
            setHasChanges(false);
            toast.success('PDF-Einstellungen gespeichert');
        },
        onError: (err) => {
            toast.error('Fehler beim Speichern: ' + err.message);
        },
    });

    // Logo upload
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate
        if (!file.type.startsWith('image/')) {
            toast.error('Bitte nur Bilddateien hochladen');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Maximale Dateigröße: 2 MB');
            return;
        }

        setUploading(true);
        try {
            // Convert to base64 data URL for simplicity (no Supabase Storage needed)
            const reader = new FileReader();
            reader.onload = () => {
                updateField('header_logo_url', reader.result);
                setUploading(false);
                toast.success('Logo hochgeladen');
            };
            reader.onerror = () => {
                setUploading(false);
                toast.error('Fehler beim Lesen der Datei');
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setUploading(false);
            toast.error('Upload fehlgeschlagen: ' + err.message);
        }
    };

    // Vorschau: exakt die produktiven Erzeuger mit Musterdaten + ungespeicherten Werten
    const getPreviewHtml = () => vorschauHtml(activeTyp, settings);
    const activeLabel = DOKUMENT_TYPEN.find((t) => t.id === activeTyp)?.label || '';

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-brand-700" />
            </div>
        );
    }

    return (
        <PageShell
          icon={<FileText className="w-5 h-5" />}
          title="PDF-Einstellungen"
          subtitle="Briefkopf, Firmendaten und Texte aller Dokumente"
          actions={(
            <>
              <Button
                variant="outline"
                onClick={() => setShowPreview(!showPreview)}
                aria-label="Vorschau"
                className="gap-2 h-11 sm:h-10 px-3"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vorschau</span>
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!hasChanges || saveMutation.isPending}
                aria-label="Speichern"
                className="gap-2 h-11 sm:h-10 px-3 bg-brand-900 hover:bg-brand-800"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Speichern</span>
              </Button>
            </>
          )}
        >

            {loadError && (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Einstellungen konnten nicht geladen werden: {loadError.message}
                </div>
            )}
            {loaded?.duplicates > 0 && (
                <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {loaded.duplicates + 1} Einstellungs-Datensätze gefunden – es gilt der älteste. Nichts wurde gelöscht.
                </div>
            )}

            {/* Preview iframe */}
            {showPreview && (
                <Card className="overflow-hidden">
                    <div className="bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600 border-b">
                        PDF-Vorschau ({activeLabel})
                    </div>
                    <iframe
                        srcDoc={getPreviewHtml()}
                        className="w-full border-0"
                        style={{ height: '700px' }}
                        title="PDF Preview"
                    />
                </Card>
            )}

            {/* ===== HEADER BANNER ===== */}
            <Section icon={ImageIcon} title="Kopfzeile (Header Banner)">
                {/* Logo Upload */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600">Firmenlogo</label>
                    <div className="flex items-center gap-4">
                        {settings.header_logo_url ? (
                            <div className="relative w-48 h-16 bg-slate-800 rounded-lg overflow-hidden flex items-center justify-center p-2">
                                <img
                                    src={settings.header_logo_url}
                                    alt="Logo"
                                    className="max-w-full max-h-full object-contain"
                                />
                            </div>
                        ) : (
                            <div className="w-48 h-16 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                                <span className="text-xs text-slate-400">Kein Logo</span>
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="gap-2"
                            >
                                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                Logo hochladen
                            </Button>
                            {settings.header_logo_url && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateField('header_logo_url', '')}
                                    className="text-red-500 hover:text-red-700 text-xs"
                                >
                                    Logo entfernen
                                </Button>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleLogoUpload}
                            />
                            <p className="text-xs text-slate-400">PNG oder JPG, max. 2 MB</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Field
                        label="Links Titel"
                        value={settings.header_left_title}
                        onChange={(v) => updateField('header_left_title', v)}
                        placeholder="I M INMOTION"
                    />
                    <Field
                        label="Rechts Titel"
                        value={settings.header_right_title}
                        onChange={(v) => updateField('header_right_title', v)}
                        placeholder="GROSSHANDEL | HORECA"
                    />
                    <Field
                        label="Links Untertitel"
                        value={settings.header_left_tagline}
                        onChange={(v) => updateField('header_left_tagline', v)}
                        placeholder="IMMER IN BEWEGUNG"
                    />
                    <Field
                        label="Rechts Untertitel"
                        value={settings.header_right_subtitle}
                        onChange={(v) => updateField('header_right_subtitle', v)}
                        placeholder="VERPACKUNG | HYGIENE | ..."
                    />
                </div>
            </Section>

            {/* ===== COMPANY INFO ===== */}
            <Section icon={Building2} title="Firmeninformationen">
                <div className="grid grid-cols-2 gap-4">
                    <Field
                        label="Firmenname"
                        value={settings.company_name}
                        onChange={(v) => updateField('company_name', v)}
                    />
                    <Field
                        label="Ansprechpartner"
                        value={settings.ansprechpartner}
                        onChange={(v) => updateField('ansprechpartner', v)}
                    />
                    <Field
                        label="Straße"
                        value={settings.street}
                        onChange={(v) => updateField('street', v)}
                    />
                    <div className="grid grid-cols-3 gap-2">
                        <Field
                            label="PLZ"
                            value={settings.zip}
                            onChange={(v) => updateField('zip', v)}
                        />
                        <div className="col-span-2">
                            <Field
                                label="Stadt"
                                value={settings.city}
                                onChange={(v) => updateField('city', v)}
                            />
                        </div>
                    </div>
                    <Field
                        label="Telefon"
                        value={settings.phone}
                        onChange={(v) => updateField('phone', v)}
                    />
                    <Field
                        label="E-Mail"
                        value={settings.email}
                        onChange={(v) => updateField('email', v)}
                    />
                    <Field
                        label="Website"
                        value={settings.website}
                        onChange={(v) => updateField('website', v)}
                    />
                    <Field
                        label="Land"
                        value={settings.country}
                        onChange={(v) => updateField('country', v)}
                    />
                </div>

                <div className="border-t pt-4 mt-2">
                    <p className="text-xs text-slate-400 mb-3 uppercase font-semibold tracking-wider">Handelsregister</p>
                    <div className="grid grid-cols-2 gap-4">
                        <Field
                            label="Registergericht"
                            value={settings.registry_city}
                            onChange={(v) => updateField('registry_city', v)}
                        />
                        <Field
                            label="HR-Nr."
                            value={settings.hr_nr}
                            onChange={(v) => updateField('hr_nr', v)}
                        />
                        <Field
                            label="Steuer-Nr."
                            value={settings.steuer_nr}
                            onChange={(v) => updateField('steuer_nr', v)}
                        />
                        <Field
                            label="Geschäftsführer"
                            value={settings.geschaeftsfuehrer}
                            onChange={(v) => updateField('geschaeftsfuehrer', v)}
                        />
                    </div>
                </div>
            </Section>

            {/* ===== BANK DETAILS ===== */}
            <Section icon={CreditCard} title="Bankverbindung">
                <div className="grid grid-cols-3 gap-4">
                    <Field
                        label="Bankname"
                        value={settings.bank_name}
                        onChange={(v) => updateField('bank_name', v)}
                    />
                    <Field
                        label="IBAN"
                        value={settings.iban}
                        onChange={(v) => updateField('iban', v)}
                    />
                    <Field
                        label="BIC"
                        value={settings.bic}
                        onChange={(v) => updateField('bic', v)}
                    />
                </div>
            </Section>

            {/* ===== PDF TEXTS ===== */}
            <Section icon={MessageSquare} title="Texte je Dokument">
                <VorlagenEditor
                    settings={settings}
                    typ={activeTyp}
                    onTypChange={setActiveTyp}
                    onChange={(next) => { setSettings(next); setHasChanges(true); }}
                />
            </Section>

            {/* Floating Save Bar */}
            {hasChanges && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                    <Button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                        className="gap-2 bg-brand-900 hover:bg-brand-800 shadow-xl px-8 py-3 text-base rounded-full"
                    >
                        {saveMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Änderungen speichern
                    </Button>
                </div>
            )}
        </PageShell>
    );
}
